/**
 * Sign-in errors that need their own wording (both designs, licence and QR flows).
 *
 * - 429 `otp.rate_limited` (send-otp by licence, qr/send-otp, qr/verify-otp): details.retry_after = seconds
 *   to wait. The app shows a live countdown instead of the server sentence (its number goes stale).
 * - 429 on verify-otp (too many wrong codes): details.retry_after too, but it is a lockout of the code
 *   check, so it keeps the «محاولات كثيرة جدًا» wording.
 * - 409 `qr.identity.exists` (qr/send-otp, qr/verify-otp): the licence already has an account; the doctor
 *   signs in with the licence and the phone on file (the QR path never takes over an existing account).
 */

export const OTP_RATE_LIMITED = "otp.rate_limited";
export const QR_IDENTITY_EXISTS = "qr.identity.exists";

/** Why the sign-in is waiting: too many code requests, too many wrong codes, or too many tries in general. */
export type WaitKind = "send" | "verify" | "generic";

/** Seconds to wait from an error's details (0 when there is nothing to wait for). */
export function retryAfterOf(details: any): number {
  const n = Number(details?.retry_after);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 0;
}

export function isIdentityExists(err: { message_code?: string | null; status?: number | null } | null | undefined): boolean {
  return !!err && err.message_code === QR_IDENTITY_EXISTS;
}

/** Translation keys for the wait box of each kind (title + the line before the countdown). */
export function waitKeys(kind: WaitKind): { title: string; body: string } {
  if (kind === "send") return { title: "otp_send_limited_title", body: "otp_send_limited_retry_in" };
  if (kind === "verify") return { title: "otp_locked_title", body: "otp_locked_retry_in" };
  return { title: "otp_wait_title", body: "otp_wait_retry_in" };
}

/** Wait kind for a 429 on a send-code request vs a code check (by message code). */
export function waitKindOf(action: "send" | "verify", messageCode?: string | null): WaitKind {
  if (action === "send") return "send";
  return messageCode === OTP_RATE_LIMITED ? "generic" : "verify";
}
